import React, { useState, useEffect, useRef } from 'react'
import { css } from '@emotion/core'
import { colors, H1, Paragraph, Row, useOverlay, WhiteTheme } from '@fs/zion-ui'
import { ContentCompose } from '@fs/zion-icon/dist/cjs/icons'
import { useTranslation } from 'react-i18next'
import CaptionOverlay from '../CaptionOverlay/CaptionOverlay'

import { useMemoryBook } from '../../../../../providers/MemoryBookProvider'
import { getHasDarkBackground } from '../../../../../helpers/getHasDarkBackground'

const pencilBarCss = (isEmpty, isHeading, hasDarkBackground, fillHeight, alwaysShowBackground) => {
  let background
  if (isEmpty || alwaysShowBackground) {
    background = hasDarkBackground ? colors.gray80 : colors.gray03
  } else {
    background = 'transparent'
  }
  return css`
    display: flex;
    justify-content: start;
    align-items: center;
    padding: ${isHeading ? '12px' : '6px'};
    background: ${background};
    width: 100%;
    ${fillHeight ? 'height: 100%;' : ''}
    border: none;
    position: relative;
    text-align: start;

    &:hover {
      transition: 0.3s ease-in-out;
      background-color: ${hasDarkBackground ? colors.gray80 : colors.gray03}; /* Subtle background change on hover */
    }

    &:focus {
      outline: 3px solid black;
      outline-offset: 2px;
    }

    h1 {
      margin-inline-start: 0; /* Reset the negative margin for headings to prevent wrapping early */
    }

    @media print {
      background: transparent;
    }
  `
}

export default function Caption({
  caption = '',
  title = '',
  pageNumber,
  positionIndex,
  isBackPage = false,
  isHeading = false,
  isPrintView = false,
  customLayoutElementId,
  onUpdateCustomLayoutElementData,
  maxLength,
  fillHeight = false,
  alwaysShowBackground = false,
  onCancelPlaceholder,
}) {
  const [t] = useTranslation()
  const [draftCaptionState, setDraftCaptionState] = useState('')
  const [draftTitleState, setDraftTitleState] = useState('')
  const captionOverlay = useOverlay()

  const { memoryBook } = useMemoryBook()
  const hasDarkBackground = getHasDarkBackground(pageNumber, memoryBook)

  // Track if overlay was auto-opened and initial mount state
  const hasAutoOpenedRef = useRef(false)
  const prevIsOpenRef = useRef(false)
  const isInitialMountRef = useRef(true)

  const textFieldOnChange = (e) => {
    setDraftCaptionState(e.target.value)
  }

  const titleFieldOnChange = (e) => {
    setDraftTitleState(e.target.value)
  }

  const handleOpen = (e) => {
    setDraftCaptionState(caption)
    setDraftTitleState(title)
    captionOverlay.open(e)
  }

  const isCaptionEmpty = caption.length === 0
  const isTitleEmpty = title.length === 0
  const isEmpty = isHeading ? isTitleEmpty : isCaptionEmpty

  // Auto-open overlay when empty on initial mount (for freeform elements)
  useEffect(() => {
    const isFreeformElement = Boolean(customLayoutElementId && onUpdateCustomLayoutElementData)

    if (isFreeformElement && isInitialMountRef.current && isEmpty && !hasAutoOpenedRef.current) {
      hasAutoOpenedRef.current = true
      setDraftCaptionState(caption)
      setDraftTitleState(title)
      captionOverlay.open()
    }

    isInitialMountRef.current = false
  }, [isEmpty, caption, title, captionOverlay, customLayoutElementId, onUpdateCustomLayoutElementData])

  // Detect cancellation (overlay closes without save on empty freeform elements)
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current
    const isNowClosed = !captionOverlay.isOpen
    const isFreeformElement = Boolean(customLayoutElementId && onUpdateCustomLayoutElementData)

    if (wasOpen && isNowClosed && isFreeformElement && isEmpty && onCancelPlaceholder) {
      // User canceled without saving on an empty element
      onCancelPlaceholder()
    }

    prevIsOpenRef.current = captionOverlay.isOpen
  }, [captionOverlay.isOpen, isEmpty, onCancelPlaceholder, customLayoutElementId, onUpdateCustomLayoutElementData])

  if (isPrintView && isEmpty) {
    return null
  }

  let titleText = ''
  if (isHeading) {
    if (isTitleEmpty) {
      titleText = t('Caption.addTitle.label', 'Add a Title')
    } else {
      titleText = t('Caption.editTitle.label', 'Edit a Title')
    }
  } else if (isCaptionEmpty) {
    titleText = t('Caption.addCaption.label', 'Add a Caption')
  } else {
    titleText = t('Caption.editCaption.label', 'Edit a Caption')
  }

  const placeholderCaptionText = caption || t('TextPicker.addText.label', 'Add Text')
  const placeholderTitleText = title || t('TextPicker.addText.label', 'Add Text')

  return (
    <>
      <button
        type="button"
        onClick={handleOpen}
        css={pencilBarCss(isEmpty, isHeading, hasDarkBackground, fillHeight, alwaysShowBackground)}
        aria-label={titleText}
      >
        <Row gutters="xs" alignY="middle" alignX="start">
          {!isPrintView && isEmpty && (
            <div>
              <ContentCompose color={colors.gray40} size="xs" />
            </div>
          )}
          {hasDarkBackground ? (
            <WhiteTheme>
              {isHeading && <H1 size={isBackPage ? 'H5' : 'H4'}>{placeholderTitleText}</H1>}
              {!isHeading && <Paragraph size="xs">{placeholderCaptionText}</Paragraph>}
            </WhiteTheme>
          ) : (
            <>
              {isHeading && <H1 size={isBackPage ? 'H5' : 'H4'}>{placeholderTitleText}</H1>}
              {!isHeading && <Paragraph size="xs">{placeholderCaptionText}</Paragraph>}
            </>
          )}
        </Row>
      </button>

      {/* PencilOverlay */}
      {(captionOverlay.isOpen || captionOverlay.transitioning) && (
        <CaptionOverlay
          overlay={captionOverlay}
          captionState={draftCaptionState}
          textFieldOnChange={textFieldOnChange}
          titleState={draftTitleState}
          titleFieldOnChange={titleFieldOnChange}
          isHeading={isHeading}
          positionIndex={positionIndex}
          pageNumber={pageNumber}
          titleText={titleText}
          customLayoutElementId={customLayoutElementId}
          onUpdateCustomLayoutElementData={onUpdateCustomLayoutElementData}
          maxLength={maxLength}
        />
      )}
    </>
  )
}
